import React, { useState, useEffect, useRef, useMemo } from "react";
import LoadingAnimation from "../LoadingAnimation";
import { toast } from "react-toastify";
import JoditEditor from "jodit-react";

import { db } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

// ─────────────────────────────────────────────
// RICH TEXT EDITOR COMPONENT
// ─────────────────────────────────────────────

const RichTextEditor = ({ value, onChange }) => {
  const editor = useRef(null);

  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: "Write job description here...",
      theme: "default",
      toolbar: true,
      toolbarAdaptive: true,
      toolbarSticky: false,
      buttons: [
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "ul",
        "ol",
        "indent",
        "outdent",
        "align",
        "font",
        "fontsize",
        "image",
        "link",
        "brush",
        "eraser",
      ],
      showXPathInStatusbar: false,
      showWordsCounter: true,
      showCharsCounter: true,
      spellcheck: true,
      minHeight: 300,
    }),
    []
  );

  return (
    <JoditEditor
      ref={editor}
      value={value}
      config={config}
      tabIndex={1}
      onBlur={(newContent) => onChange(newContent)}
      onChange={() => { }}
    />
  );
};

// ─────────────────────────────────────────────
// DEFAULT DATA
// ─────────────────────────────────────────────

const DEFAULT_JOB_TITLES = [
  "Adobe Photoshop & Illustrator",
  "Adobe Photoshop & Figma",
  "Graphics Designer Intern",
  "Front-End Developer",
  "Junior Front-End Developer",
  "Motion Graphic Expert",
];

const DEFAULT_DEPARTMENTS = [
  "Marketing Ads",
  "Website Design",
  "Website Development",
];

const DEFAULT_SKILL_OPTIONS = {
  "Adobe Photoshop & Illustrator": {
    "Marketing Ads": [
      "Adobe Photoshop",
      "Adobe Illustrator",
      "Creative Campaigns",
      "Banner Design",
      "Social Media Graphics",
    ],
    "Website Design": [
      "Figma",
      "UI/UX Design",
      "Color Theory",
      "Layout Composition",
    ],
  },
  "Adobe Photoshop & Figma": {
    "Website Design": ["Figma", "UI/UX Prototyping", "Photoshop", "Typography"],
    "Website Development": ["Responsive Layouts", "Web Assets Design"],
  },
  "Front-End Developer": {
    "Website Development": [
      "React.js",
      "JavaScript (ES6+)",
      "HTML5 / CSS3",
      "Firebase",
      "REST APIs",
    ],
    "Website Design": ["Figma", "Pixel Perfect Conversion"],
  },
  "Graphics Designer Intern": {
    "Marketing Ads": [
      "Photoshop",
      "Canva",
      "Poster Design",
      "Social Media Posts",
    ],
    "Website Design": ["Figma", "UI Elements"],
  },
  "Motion Graphic Expert": {
    "Marketing Ads": [
      "After Effects",
      "Premiere Pro",
      "Animation",
      "Storyboarding",
    ],
  },
  "Junior Front-End Developer": {
    "Website Development": [
      "HTML",
      "CSS",
      "JavaScript",
      "React.js",
      "Bootstrap",
    ],
  },
};

const PostJob = () => {
  const [jobData, setJobData] = useState({
    date: "",
    category: "",
    title: "",
    requirements: [],
    salary: "",
    description: "",
    location: "",
    gender: "",
    isHidden: false,
  });

  const [jobs, setJobs] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletePopup, setDeletePopup] = useState({
    visible: false,
    jobId: null,
  });
  const [showTitleDropdown, setShowTitleDropdown] = useState(false);
  const [currentSkillInput, setCurrentSkillInput] = useState("");
  const skillInputRef = useRef(null);
  const formTopRef = useRef(null);

  // Description modal state
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [tempDescription, setTempDescription] = useState("");

  // Dynamic data management
  const [jobTitles, setJobTitles] = useState(DEFAULT_JOB_TITLES);
  const [departments, setDepartments] = useState(DEFAULT_DEPARTMENTS);
  const [skillOptions, setSkillOptions] = useState(DEFAULT_SKILL_OPTIONS);

  // Add modals
  const [showTitleModal, setShowTitleModal] = useState(false);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [newTitleName, setNewTitleName] = useState("");
  const [newDepartmentName, setNewDepartmentName] = useState("");

  // Manage modals
  const [showManageTitlesModal, setShowManageTitlesModal] = useState(false);
  const [showManageDepartmentsModal, setShowManageDepartmentsModal] =
    useState(false);

  // Inline editing state
  const [editingTitleIndex, setEditingTitleIndex] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState("");
  const [editingDepartmentIndex, setEditingDepartmentIndex] = useState(null);
  const [editingDepartmentValue, setEditingDepartmentValue] = useState("");

  // Delete confirmations
  const [deleteTitleConfirm, setDeleteTitleConfirm] = useState(null);
  const [deleteDepartmentConfirm, setDeleteDepartmentConfirm] = useState(null);

  // Modal refs for outside click
  const manageTitlesOverlayRef = useRef(null);
  const manageDepartmentsOverlayRef = useRef(null);
  const titleModalOverlayRef = useRef(null);
  const departmentModalOverlayRef = useRef(null);
  const deletePopupOverlayRef = useRef(null);

  // Default location
  const defaultLocation =
    "B-343, Pagganwala Street, Near Cheema Masjid, Shadman Colony, Gujrat, Pakistan.";

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setJobData((prev) => ({ ...prev, date: today, location: defaultLocation }));
    fetchJobs();
    loadCustomData();
  }, []);

  const loadCustomData = () => {
    const savedTitles = localStorage.getItem("jobTitles");
    if (savedTitles)
      setJobTitles(
        JSON.parse(savedTitles).map((t) => t.replace(/&#x2F;/gi, "/"))
      );
    const savedDepartments = localStorage.getItem("departments");
    if (savedDepartments) setDepartments(JSON.parse(savedDepartments));
    const savedSkillOptions = localStorage.getItem("skillOptions");
    if (savedSkillOptions) setSkillOptions(JSON.parse(savedSkillOptions));
  };

  const saveCustomData = (titles, depts, skills) => {
    localStorage.setItem("jobTitles", JSON.stringify(titles));
    localStorage.setItem("departments", JSON.stringify(depts));
    localStorage.setItem("skillOptions", JSON.stringify(skills));
  };

  const fetchJobs = async () => {
    setLoading(true);
    const querySnapshot = await getDocs(collection(db, "jobPosts"));
    const jobList = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      title: (doc.data().title || "").replace(/&#x2F;/gi, "/"),
    }));
    setJobs(jobList);
    setLoading(false);
  };

  // ── ADD HANDLERS ──────────────────────────────────────

  const handleAddTitle = () => {
    if (!newTitleName.trim()) {
      toast.error("Please enter a job title");
      return;
    }
    const titleName = newTitleName.trim();
    if (jobTitles.includes(titleName)) {
      toast.error("Job title already exists!");
      return;
    }
    const updatedTitles = [...jobTitles, titleName];
    const updatedSkills = { ...skillOptions, [titleName]: {} };
    setJobTitles(updatedTitles);
    setSkillOptions(updatedSkills);
    saveCustomData(updatedTitles, departments, updatedSkills);
    toast.success(`Job title "${titleName}" added successfully!`);
    setNewTitleName("");
    setShowTitleModal(false);
  };

  const handleAddDepartment = () => {
    if (!newDepartmentName.trim()) {
      toast.error("Please enter a department name");
      return;
    }
    const deptName = newDepartmentName.trim();
    if (departments.includes(deptName)) {
      toast.error("Department already exists!");
      return;
    }
    const updatedDepartments = [...departments, deptName];
    const updatedSkills = { ...skillOptions };
    Object.keys(updatedSkills).forEach((title) => {
      updatedSkills[title][deptName] = [];
    });
    setDepartments(updatedDepartments);
    setSkillOptions(updatedSkills);
    saveCustomData(jobTitles, updatedDepartments, updatedSkills);
    toast.success(`Department "${deptName}" added successfully!`);
    setNewDepartmentName("");
    setShowDepartmentModal(false);
  };

  // ── MANAGE TITLES CRUD ────────────────────────────────

  const handleEditTitleSave = (index) => {
    const trimmed = editingTitleValue.trim();
    if (!trimmed) {
      toast.error("Job title cannot be empty");
      return;
    }
    const oldName = jobTitles[index];
    if (trimmed === oldName) {
      setEditingTitleIndex(null);
      return;
    }
    if (jobTitles.includes(trimmed)) {
      toast.error("Job title already exists!");
      return;
    }
    const updatedTitles = [...jobTitles];
    updatedTitles[index] = trimmed;
    const updatedSkills = { ...skillOptions };
    updatedSkills[trimmed] = updatedSkills[oldName];
    delete updatedSkills[oldName];
    setJobTitles(updatedTitles);
    setSkillOptions(updatedSkills);
    saveCustomData(updatedTitles, departments, updatedSkills);
    toast.success("Job title updated!");
    setEditingTitleIndex(null);
    setEditingTitleValue("");
  };

  const handleDeleteTitle = (index) => {
    const name = jobTitles[index];
    const updatedTitles = jobTitles.filter((_, i) => i !== index);
    const updatedSkills = { ...skillOptions };
    delete updatedSkills[name];
    setJobTitles(updatedTitles);
    setSkillOptions(updatedSkills);
    saveCustomData(updatedTitles, departments, updatedSkills);
    toast.success(`Job title "${name}" deleted!`);
    setDeleteTitleConfirm(null);
  };

  // ── MANAGE DEPARTMENTS CRUD ────────────────────────────

  const handleEditDepartmentSave = (index) => {
    const trimmed = editingDepartmentValue.trim();
    if (!trimmed) {
      toast.error("Department cannot be empty");
      return;
    }
    const oldName = departments[index];
    if (trimmed === oldName) {
      setEditingDepartmentIndex(null);
      return;
    }
    if (departments.includes(trimmed)) {
      toast.error("Department already exists!");
      return;
    }
    const updatedDepartments = [...departments];
    updatedDepartments[index] = trimmed;
    const updatedSkills = { ...skillOptions };
    Object.keys(updatedSkills).forEach((title) => {
      if (updatedSkills[title][oldName]) {
        updatedSkills[title][trimmed] = updatedSkills[title][oldName];
        delete updatedSkills[title][oldName];
      }
    });
    setDepartments(updatedDepartments);
    setSkillOptions(updatedSkills);
    saveCustomData(jobTitles, updatedDepartments, updatedSkills);
    toast.success("Department updated!");
    setEditingDepartmentIndex(null);
    setEditingDepartmentValue("");
  };

  const handleDeleteDepartment = (index) => {
    const name = departments[index];
    const updatedDepartments = departments.filter((_, i) => i !== index);
    const updatedSkills = { ...skillOptions };
    Object.keys(updatedSkills).forEach((title) => {
      delete updatedSkills[title][name];
    });
    setDepartments(updatedDepartments);
    setSkillOptions(updatedSkills);
    saveCustomData(jobTitles, updatedDepartments, updatedSkills);
    toast.success(`Department "${name}" deleted!`);
    setDeleteDepartmentConfirm(null);
  };

  // ── FORM HANDLERS ─────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setJobData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "title" ? { requirements: [] } : {}),
    }));
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setJobData((prev) => ({
      ...prev,
      title: value,
      requirements: [],
    }));
    setShowTitleDropdown(true);
  };

  const handleTitleSelect = (title) => {
    setJobData((prev) => ({
      ...prev,
      title: title,
      requirements: [],
    }));
    setShowTitleDropdown(false);
  };

  const getFilteredTitles = () => {
    if (!jobData.title) return jobTitles;
    return jobTitles.filter((title) =>
      title.toLowerCase().includes(jobData.title.toLowerCase())
    );
  };

  const handleSkillKeyDown = (e) => {
    if (e.key === "Enter" && currentSkillInput.trim()) {
      e.preventDefault();
      const newSkill = currentSkillInput.trim();
      if (!jobData.requirements.includes(newSkill)) {
        setJobData((prev) => ({
          ...prev,
          requirements: [...prev.requirements, newSkill],
        }));
      }
      setCurrentSkillInput("");
    } else if (
      e.key === "Backspace" &&
      currentSkillInput === "" &&
      jobData.requirements.length > 0
    ) {
      e.preventDefault();
      setJobData((prev) => ({
        ...prev,
        requirements: prev.requirements.slice(0, -1),
      }));
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setJobData((prev) => ({
      ...prev,
      requirements: prev.requirements.filter(
        (skill) => skill !== skillToRemove
      ),
    }));
  };

  const handleSkillSelect = (skill) => {
    setJobData((prev) => {
      const alreadySelected = prev.requirements.includes(skill);
      return {
        ...prev,
        requirements: alreadySelected
          ? prev.requirements.filter((s) => s !== skill)
          : [...prev.requirements, skill],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !jobData.title ||
      !jobData.category ||
      jobData.requirements.length === 0 ||
      !jobData.gender
    ) {
      toast.error(
        "⚠️ Please fill all required fields and select at least one skill."
      );
      return;
    }

    setLoading(true);

    const finalJobData = {
      ...jobData,
      location: jobData.location?.trim() || defaultLocation,
      salary:
        jobData.salary?.trim() || "Salary will be decided after interview",
      isHidden: jobData.isHidden || false,
    };

    try {
      if (editingId) {
        const docRef = doc(db, "jobPosts", editingId);
        await updateDoc(docRef, finalJobData);
        setEditingId(null);
        toast.success("Job updated successfully!");
      } else {
        await addDoc(collection(db, "jobPosts"), {
          ...finalJobData,
          createdAt: serverTimestamp(),
        });
        toast.success("Job posted successfully!");
      }

      fetchJobs();

      const today = new Date().toISOString().split("T")[0];
      setJobData({
        date: today,
        category: "",
        title: "",
        requirements: [],
        salary: "",
        description: "",
        location: defaultLocation,
        gender: "",
        isHidden: false,
      });
      setCurrentSkillInput("");
    } catch (error) {
      console.error("Error adding/updating job:", error);
      toast.error("❌ Error saving job post!");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (job) => {
    setJobData(job);
    setEditingId(job.id);

    if (formTopRef.current) {
      formTopRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleDelete = (id) => {
    setDeletePopup({ visible: true, jobId: id });
  };

  const handleToggleVisibility = async (job) => {
    try {
      const docRef = doc(db, "jobPosts", job.id);
      await updateDoc(docRef, { isHidden: !job.isHidden });
      fetchJobs();
      toast.success(
        !job.isHidden
          ? `"${job.title}" is now hidden from public."`
          : `"${job.title}" is now visible to public."`
      );
    } catch (error) {
      console.error("Error toggling visibility:", error);
      toast.error("Failed to update visibility.");
    }
  };

  const confirmDelete = async () => {
    if (!deletePopup.jobId) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, "jobPosts", deletePopup.jobId));
      fetchJobs();
      toast.success("🗑️ Job deleted successfully!");
    } catch (error) {
      console.error("Error deleting job:", error);
      toast.error("❌ Error deleting job!");
    } finally {
      setLoading(false);
      setDeletePopup({ visible: false, jobId: null });
    }
  };

  const cancelDelete = () => {
    setDeletePopup({ visible: false, jobId: null });
  };

  // Close modals when clicking outside
  const handleOverlayClick = (e, modalRef, closeFunction) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      closeFunction();
    }
  };

  const relevantSkills = skillOptions[jobData.title]?.[jobData.category] || [];

  // Modal Styles
  const modalStyles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(4px)",
    },
    container: {
      background: "white",
      padding: "32px",
      borderRadius: "16px",
      width: "420px",
      maxWidth: "90%",
      boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
    },
    wideContainer: {
      background: "white",
      padding: "32px",
      borderRadius: "16px",
      width: "560px",
      maxWidth: "95%",
      maxHeight: "80vh",
      overflowY: "auto",
      boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
    },
    input: {
      width: "100%",
      padding: "12px",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      fontSize: "14px",
      marginBottom: "20px",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      padding: "12px",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      fontSize: "14px",
      marginBottom: "20px",
      boxSizing: "border-box",
    },
    buttonPrimary: {
      background: "#f13737",
      color: "white",
      padding: "10px 20px",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      fontWeight: "600",
      marginRight: "12px",
      fontSize: "13px",
    },
    buttonSecondary: {
      backgroundColor: "transparent",
      color: "#718096",
      padding: "10px 20px",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: "13px",
    },
    iconBtn: (color = "#FF8000") => ({
      background: "none",
      border: "none",
      cursor: "pointer",
      color,
      padding: "4px 6px",
      borderRadius: "4px",
      display: "inline-flex",
      alignItems: "center",
    }),
    listItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 12px",
      borderRadius: "8px",
      marginBottom: "6px",
      background: "#f9fafb",
      border: "1px solid #e2e8f0",
      gap: "8px",
    },
    editInput: {
      flex: 1,
      padding: "6px 10px",
      borderRadius: "6px",
      border: "1px solid #FF8000",
      fontSize: "14px",
      outline: "none",
    },
  };

  const PlusIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );

  const EditIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 512 512"
      fill="currentColor"
    >
      <path d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1L377.9 88L407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9l46.1 46.1l-134.3 134.2c-2.9 2.9-6.5 5-10.4 6.1L186.9 325l16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25c-28.1-28.1-73.7-28.1-101.8 0M88 64c-48.6 0-88 39.4-88 88v272c0 48.6 39.4 88 88 88h272c48.6 0 88-39.4 88-88V312c0-13.3-10.7-24-24-24s-24 10.7-24 24v112c0 22.1-17.9 40-40 40H88c-22.1 0-40-17.9-40-40V152c0-22.1 17.9-40 40-40h112c13.3 0 24-10.7 24-24s-10.7-24-24-24z" />
    </svg>
  );

  const TrashIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zm2.46-7.12l1.41-1.41L12 12.59l2.12-2.12l1.41 1.41L13.41 14l2.12 2.12l-1.41 1.41L12 15.41l-2.12 2.12l-1.41-1.41L10.59 14zM15.5 4l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  );

  const CheckIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  );

  const XIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ cursor: "pointer" }}
    >
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );

  const GearIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3"></circle>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
    </svg>
  );

  return (
    <div className="postjob-container" ref={formTopRef}>
      {/* Delete Job Popup */}
      {deletePopup.visible && (
        <div
          style={modalStyles.overlay}
          ref={deletePopupOverlayRef}
          onClick={(e) =>
            handleOverlayClick(e, deletePopupOverlayRef, cancelDelete)
          }
        >
          <div
            style={modalStyles.container}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                background: "linear-gradient(135deg, #f56565 0%, #ed8936 100%)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <span
                style={{ color: "white", fontSize: "20px", fontWeight: "bold" }}
              >
                !
              </span>
            </div>
            <h3
              style={{
                margin: "0 0 12px 0",
                color: "#2D3748",
                fontSize: "18px",
              }}
            >
              Confirm Delete
            </h3>
            <p
              style={{
                margin: "0 0 28px 0",
                color: "#718096",
                fontSize: "14px",
                lineHeight: "1.4",
              }}
            >
              Are you sure you want to delete this job post?
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                style={{
                  ...modalStyles.buttonSecondary,
                  color: "#000 !important",
                }}
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button style={modalStyles.buttonPrimary} onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Job Title Modal */}
      {showTitleModal && (
        <div
          style={modalStyles.overlay}
          ref={titleModalOverlayRef}
          onClick={(e) =>
            handleOverlayClick(e, titleModalOverlayRef, () =>
              setShowTitleModal(false)
            )
          }
        >
          <div
            style={modalStyles.container}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, color: "#2D3748" }}>Add New Job Title</h3>
              <button
                style={modalStyles.iconBtn("#718096")}
                onClick={() => setShowTitleModal(false)}
              >
                <XIcon />
              </button>
            </div>
            <input
              type="text"
              placeholder="Enter job title (e.g., Senior React Developer)"
              value={newTitleName}
              onChange={(e) => setNewTitleName(e.target.value)}
              style={modalStyles.input}
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleAddTitle()}
            />
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                style={modalStyles.buttonPrimary}
                onClick={handleAddTitle}
              >
                Add Title
              </button>
              <button
                style={modalStyles.buttonSecondary}
                onClick={() => setShowTitleModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Department Modal */}
      {showDepartmentModal && (
        <div
          style={modalStyles.overlay}
          ref={departmentModalOverlayRef}
          onClick={(e) =>
            handleOverlayClick(e, departmentModalOverlayRef, () =>
              setShowDepartmentModal(false)
            )
          }
        >
          <div
            style={modalStyles.container}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, color: "#2D3748" }}>
                Add New Department
              </h3>
              <button
                style={modalStyles.iconBtn("#718096")}
                onClick={() => setShowDepartmentModal(false)}
              >
                <XIcon />
              </button>
            </div>
            <input
              type="text"
              placeholder="Enter department name (e.g., HR Department)"
              value={newDepartmentName}
              onChange={(e) => setNewDepartmentName(e.target.value)}
              style={modalStyles.input}
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleAddDepartment()}
            />
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                style={modalStyles.buttonPrimary}
                onClick={handleAddDepartment}
              >
                Add Department
              </button>
              <button
                style={modalStyles.buttonSecondary}
                onClick={() => setShowDepartmentModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MANAGE JOB TITLES MODAL */}
      {showManageTitlesModal && (
        <div
          style={modalStyles.overlay}
          ref={manageTitlesOverlayRef}
          onClick={(e) =>
            handleOverlayClick(e, manageTitlesOverlayRef, () => {
              setShowManageTitlesModal(false);
              setEditingTitleIndex(null);
              setDeleteTitleConfirm(null);
            })
          }
        >
          <div
            style={modalStyles.wideContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, color: "#2D3748" }}>Manage Job Titles</h3>
              <button
                style={modalStyles.iconBtn("#718096")}
                onClick={() => {
                  setShowManageTitlesModal(false);
                  setEditingTitleIndex(null);
                  setDeleteTitleConfirm(null);
                }}
              >
                <XIcon />
              </button>
            </div>

            {jobTitles.length === 0 && (
              <p
                style={{
                  color: "#a0aec0",
                  textAlign: "center",
                  padding: "20px 0",
                }}
              >
                No job titles added yet.
              </p>
            )}

            {jobTitles.map((title, index) => (
              <div key={index}>
                {deleteTitleConfirm === index ? (
                  <div
                    style={{
                      ...modalStyles.listItem,
                      background: "#fff5f5",
                      borderColor: "#feb2b2",
                    }}
                  >
                    <span
                      style={{ flex: 1, fontSize: "14px", color: "#c53030" }}
                    >
                      Delete "<strong>{title}</strong>" and its skills?
                    </span>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button
                        style={{ ...modalStyles.iconBtn("#e53e3e") }}
                        title="Confirm Delete"
                        onClick={() => handleDeleteTitle(index)}
                      >
                        <CheckIcon />
                      </button>
                      <button
                        style={{ ...modalStyles.iconBtn("#718096") }}
                        title="Cancel"
                        onClick={() => setDeleteTitleConfirm(null)}
                      >
                        <XIcon />
                      </button>
                    </div>
                  </div>
                ) : editingTitleIndex === index ? (
                  <div style={modalStyles.listItem}>
                    <input
                      autoFocus
                      value={editingTitleValue}
                      onChange={(e) => setEditingTitleValue(e.target.value)}
                      style={modalStyles.editInput}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleEditTitleSave(index);
                        if (e.key === "Escape") setEditingTitleIndex(null);
                      }}
                    />
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button
                        style={modalStyles.iconBtn("#38a169")}
                        title="Save"
                        onClick={() => handleEditTitleSave(index)}
                      >
                        <CheckIcon />
                      </button>
                      <button
                        style={modalStyles.iconBtn("#718096")}
                        title="Cancel"
                        onClick={() => setEditingTitleIndex(null)}
                      >
                        <XIcon />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={modalStyles.listItem}>
                    <span
                      style={{
                        flex: 1,
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#2D3748",
                      }}
                    >
                      {title}
                    </span>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button
                        style={modalStyles.iconBtn("#FF8000")}
                        title="Edit"
                        onClick={() => {
                          setEditingTitleIndex(index);
                          setEditingTitleValue(title);
                        }}
                      >
                        <EditIcon />
                      </button>
                      <button
                        style={modalStyles.iconBtn("#e53e3e")}
                        title="Delete"
                        onClick={() => setDeleteTitleConfirm(index)}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div
              style={{
                marginTop: "16px",
                paddingTop: "16px",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                style={modalStyles.buttonPrimary}
                onClick={() => {
                  setShowManageTitlesModal(false);
                  setShowTitleModal(true);
                }}
              >
                + Add New Title
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MANAGE DEPARTMENTS MODAL */}
      {showManageDepartmentsModal && (
        <div
          style={modalStyles.overlay}
          ref={manageDepartmentsOverlayRef}
          onClick={(e) =>
            handleOverlayClick(e, manageDepartmentsOverlayRef, () => {
              setShowManageDepartmentsModal(false);
              setEditingDepartmentIndex(null);
              setDeleteDepartmentConfirm(null);
            })
          }
        >
          <div
            style={modalStyles.wideContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, color: "#2D3748" }}>
                Manage Departments
              </h3>
              <button
                style={modalStyles.iconBtn("#718096")}
                onClick={() => {
                  setShowManageDepartmentsModal(false);
                  setEditingDepartmentIndex(null);
                  setDeleteDepartmentConfirm(null);
                }}
              >
                <XIcon />
              </button>
            </div>

            {departments.length === 0 && (
              <p
                style={{
                  color: "#a0aec0",
                  textAlign: "center",
                  padding: "20px 0",
                }}
              >
                No departments added yet.
              </p>
            )}

            {departments.map((dept, index) => (
              <div key={index}>
                {deleteDepartmentConfirm === index ? (
                  <div
                    style={{
                      ...modalStyles.listItem,
                      background: "#fff5f5",
                      borderColor: "#feb2b2",
                    }}
                  >
                    <span
                      style={{ flex: 1, fontSize: "14px", color: "#c53030" }}
                    >
                      Delete department "<strong>{dept}</strong>" and its
                      skills?
                    </span>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button
                        style={{ ...modalStyles.iconBtn("#e53e3e") }}
                        title="Confirm Delete"
                        onClick={() => handleDeleteDepartment(index)}
                      >
                        <CheckIcon />
                      </button>
                      <button
                        style={{ ...modalStyles.iconBtn("#718096") }}
                        title="Cancel"
                        onClick={() => setDeleteDepartmentConfirm(null)}
                      >
                        <XIcon />
                      </button>
                    </div>
                  </div>
                ) : editingDepartmentIndex === index ? (
                  <div style={modalStyles.listItem}>
                    <input
                      autoFocus
                      value={editingDepartmentValue}
                      onChange={(e) =>
                        setEditingDepartmentValue(e.target.value)
                      }
                      style={modalStyles.editInput}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleEditDepartmentSave(index);
                        if (e.key === "Escape") setEditingDepartmentIndex(null);
                      }}
                    />
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button
                        style={modalStyles.iconBtn("#38a169")}
                        title="Save"
                        onClick={() => handleEditDepartmentSave(index)}
                      >
                        <CheckIcon />
                      </button>
                      <button
                        style={modalStyles.iconBtn("#718096")}
                        title="Cancel"
                        onClick={() => setEditingDepartmentIndex(null)}
                      >
                        <XIcon />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={modalStyles.listItem}>
                    <span
                      style={{
                        flex: 1,
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#2D3748",
                      }}
                    >
                      {dept}
                    </span>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button
                        style={modalStyles.iconBtn("#FF8000")}
                        title="Edit"
                        onClick={() => {
                          setEditingDepartmentIndex(index);
                          setEditingDepartmentValue(dept);
                        }}
                      >
                        <EditIcon />
                      </button>
                      <button
                        style={modalStyles.iconBtn("#e53e3e")}
                        title="Delete"
                        onClick={() => setDeleteDepartmentConfirm(index)}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div
              style={{
                marginTop: "16px",
                paddingTop: "16px",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                style={modalStyles.buttonPrimary}
                onClick={() => {
                  setShowManageDepartmentsModal(false);
                  setShowDepartmentModal(true);
                }}
              >
                + Add New Department
              </button>
            </div>
          </div>
        </div>
      )}

      <h2 className="page-heading">
        <i className="fas fa-briefcase" style={{ color: "#FF8000" }}></i>
        {editingId ? "Edit Job" : "Post a New Job"}
      </h2>

      <form onSubmit={handleSubmit} className="postjob-form">
        {/* Job Title with Add and Manage Buttons */}
        <div
          style={{ position: "relative", width: "100%", marginBottom: "16px" }}
        >
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div style={{ flex: 1, position: "relative" }}>
              <input
                type="text"
                name="title"
                placeholder="Enter or select job title (e.g., Senior React Developer)"
                value={jobData.title}
                onChange={handleTitleChange}
                onFocus={() => setShowTitleDropdown(true)}
                onBlur={() => {
                  setTimeout(() => setShowTitleDropdown(false), 200);
                }}
                required
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "16px",
                }}
              />

              {showTitleDropdown && getFilteredTitles().length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    background: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    marginTop: "4px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                >
                  {getFilteredTitles().map((title) => (
                    <div
                      key={title}
                      onMouseDown={() => handleTitleSelect(title)}
                      style={{
                        padding: "10px 12px",
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#f7fafc";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "white";
                      }}
                    >
                      {title}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowTitleModal(true)}
              title="Add Job Title"
              style={{
                width: "fit-content",
                padding: "12px",
                backgroundColor: "#fff",
                border: "1px solid #FF8000",
                borderRadius: "8px",
                color: "#FF8000",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PlusIcon />
            </button>
            <button
              type="button"
              onClick={() => setShowManageTitlesModal(true)}
              title="Manage Job Titles"
              style={{
                width: "fit-content",
                padding: "12px",
                backgroundColor: "#fff",
                border: "1px solid #718096",
                borderRadius: "8px",
                color: "#718096",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GearIcon />
            </button>
          </div>
        </div>

        {/* Department with Add and Manage Buttons */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <select
              name="category"
              value={jobData.category}
              onChange={handleChange}
              required
              style={{
                flex: 1,
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
              }}
            >
              <option value="">Select Department</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowDepartmentModal(true)}
              title="Add Department"
              style={{
                width: "fit-content",
                padding: "12px",
                backgroundColor: "#fff",
                border: "1px solid #FF8000",
                borderRadius: "8px",
                color: "#FF8000",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PlusIcon />
            </button>
            <button
              type="button"
              onClick={() => setShowManageDepartmentsModal(true)}
              title="Manage Departments"
              style={{
                width: "fit-content",
                padding: "12px",
                backgroundColor: "#fff",
                border: "1px solid #718096",
                borderRadius: "8px",
                color: "#718096",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GearIcon />
            </button>
          </div>
        </div>

        <select
          name="gender"
          value={jobData.gender}
          onChange={handleChange}
          required
          style={{
            marginBottom: "16px",
            width: "100%",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
          }}
        >
          <option value="">Who Can Apply?</option>
          <option value="Male">Male only</option>
          <option value="Female">Female only</option>
          <option value="Male & Female">Male & Female both</option>
        </select>

        {/* ── VISIBILITY TOGGLE ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            padding: "12px 16px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            backgroundColor: jobData.isHidden ? "#fff5f5" : "#f0fff4",
            transition: "background-color 0.2s",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "18px" }}>
              {jobData.isHidden ? "🔒" : "🔓"}
            </span>
            <div>
              <p style={{ margin: 0, fontWeight: "600", fontSize: "14px", color: "#2D3748" }}>
                Job Visibility
              </p>
              <p style={{ margin: 0, fontSize: "12px", color: jobData.isHidden ? "#e53e3e" : "#38a169" }}>
                {jobData.isHidden ? "Hidden — not visible to public" : "Visible — shown to public"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setJobData((prev) => ({ ...prev, isHidden: !prev.isHidden }))}
            style={{
              position: "relative",
              width: "52px",
              height: "28px",
              borderRadius: "14px",
              border: "none",
              cursor: "pointer",
              backgroundColor: jobData.isHidden ? "#e53e3e" : "#38a169",
              transition: "background-color 0.25s",
              padding: 0,
              flexShrink: 0,
            }}
            title={jobData.isHidden ? "Click to show this job" : "Click to hide this job"}
          >
            <span
              style={{
                position: "absolute",
                top: "3px",
                left: jobData.isHidden ? "3px" : "27px",
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                backgroundColor: "white",
                transition: "left 0.25s",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }}
            />
          </button>
        </div>

        {jobData.title && jobData.category && (
          <div className="skills-section" style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
              }}
            >
              Required Skills:
            </label>

            {skillOptions[jobData.title] && relevantSkills.length > 0 ? (
              <>
                <div className="skills-list" style={{ marginBottom: "16px" }}>
                  {relevantSkills.map((skill) => (
                    <label
                      key={skill}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        marginRight: "16px",
                        marginBottom: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={jobData.requirements.includes(skill)}
                        onChange={() => handleSkillSelect(skill)}
                        style={{ marginRight: "6px" }}
                      />
                      {skill}
                    </label>
                  ))}
                </div>
                <div
                  style={{
                    borderTop: "1px solid #e2e8f0",
                    paddingTop: "12px",
                    marginTop: "8px",
                  }}
                >
                  <small
                    style={{
                      color: "#718096",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    Or add custom skills:
                  </small>
                </div>
              </>
            ) : null}

            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "8px",
                backgroundColor: "white",
                cursor: "text",
              }}
              onClick={() => {
                if (skillInputRef.current) skillInputRef.current.focus();
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                {jobData.requirements.map((skill, index) => (
                  <span
                    key={index}
                    style={{
                      backgroundColor: "#FF8000",
                      color: "white",
                      padding: "4px 10px",
                      borderRadius: "20px",
                      fontSize: "14px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => handleRemoveSkill(skill)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "white",
                        cursor: "pointer",
                        fontSize: "16px",
                        padding: "0",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor =
                          "rgba(255,255,255,0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "transparent";
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  ref={skillInputRef}
                  type="text"
                  value={currentSkillInput}
                  onChange={(e) => setCurrentSkillInput(e.target.value)}
                  onKeyDown={handleSkillKeyDown}
                  placeholder={
                    jobData.requirements.length === 0
                      ? "Type a skill and press Enter..."
                      : ""
                  }
                  style={{
                    border: "none",
                    outline: "none",
                    padding: "4px",
                    fontSize: "14px",
                    flex: "1",
                    minWidth: "120px",
                  }}
                />
              </div>
            </div>
            <small
              style={{ color: "#718096", display: "block", marginTop: "6px" }}
            >
              Press Enter to add each skill. Press Backspace to remove last
              skill. Click × to remove.
            </small>
          </div>
        )}

        <input
          type="text"
          name="salary"
          placeholder="Salary (e.g. PKR:35000)"
          value={jobData.salary}
          onChange={handleChange}
          style={{
            marginBottom: "16px",
            width: "100%",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
          }}
        />

        {/* ── DESCRIPTION INPUT WITH POPUP MODAL ── */}
        <div style={{ marginBottom: "16px" }}>


          {/* Clickable preview box */}
          <div
            onClick={() => {
              setTempDescription(jobData.description);
              setShowDescriptionModal(true);
            }}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "20px",
              minHeight: "100px",
              cursor: "pointer",
              backgroundColor: "#f9fafb",
              transition: "border-color 0.2s",
              wordBreak: "break-word",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.borderColor = "#FF8000")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.borderColor = "#e2e8f0")
            }
          >
            {jobData.description ? (
              <div dangerouslySetInnerHTML={{ __html: jobData.description }} />
            ) : (
              <span style={{ color: "#a0aec0" }}>
                Click here to write job description...
              </span>
            )}
          </div>
          <small
            style={{ color: "#718096", display: "block", marginTop: "6px" }}
          >
            Click the box above to open full-screen editor
          </small>
        </div>

        {/* ── FULL SCREEN DESCRIPTION MODAL ── */}
        {showDescriptionModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDescriptionModal(false);
              }
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "16px",
                width: "90%",
                maxWidth: "1200px",
                height: "85vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#f8fafc",
                  flexShrink: 0,
                }}
              >
                <div>
                  <h3
                    style={{ margin: 0, color: "#1e293b", fontSize: "1.25rem" }}
                  >
                    Edit Job Description
                  </h3>

                </div>
                <button
                  onClick={() => setShowDescriptionModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "8px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#e2e8f0")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              {/* Modal Body - Rich Text Editor */}
              <div style={{ flex: 1, overflow: "auto", padding: "20px" }}>
                <RichTextEditor
                  value={tempDescription}
                  onChange={(val) => setTempDescription(val)}
                />
              </div>

              {/* Modal Footer */}
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                  backgroundColor: "#f8fafc",
                  flexShrink: 0,
                }}
              >
                <button
                  onClick={() => setShowDescriptionModal(false)}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "white",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f1f5f9")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "white")
                  }
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setJobData((prev) => ({
                      ...prev,
                      description: tempDescription,
                    }));
                    setShowDescriptionModal(false);
                    toast.success("Description saved!");
                  }}
                  style={{
                    padding: "10px 24px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "#FF8000",
                    color: "white",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#e67600")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#FF8000")
                  }
                >
                  Save Description
                </button>
              </div>
            </div>
          </div>
        )}

        <input
          type="text"
          name="location"
          placeholder="Enter job location (auto-filled if empty)"
          value={jobData.location}
          onChange={handleChange}
          style={{
            marginBottom: "16px",
            width: "100%",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
          }}
        />

        <input
          className="date-input"
          type="date"
          name="date"
          value={jobData.date}
          onChange={handleChange}
          required
          style={{
            marginBottom: "16px",
            width: "100%",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
          }}
        />

        <button
          type="submit"
          disabled={loading}
          style={{
            width: "fit-content",
            backgroundColor: "#FF8000",
            color: "white",
            padding: "12px 24px",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: "600",
          }}
        >
          {loading ? "Saving..." : editingId ? "Update Job" : "Post Job"}
        </button>
      </form>

      <div className="job-records">
        <h3>All Posted Jobs</h3>
        {loading ? (
          <LoadingAnimation text="Loading Jobs..." />
        ) : jobs.length === 0 ? (
          <p>No jobs posted yet.</p>
        ) : (
          <div className="table-wrapper">
            <table className="job-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Department</th>
                  <th>Salary</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id} style={{ opacity: job.isHidden ? 0.6 : 1 }}>
                    <td>{job.title}</td>
                    <td>{job.category}</td>
                    <td>{job.salary}</td>
                    <td>{job.date}</td>
                    <td>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "3px 10px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          backgroundColor: job.isHidden ? "#fed7d7" : "#c6f6d5",
                          color: job.isHidden ? "#c53030" : "#276749",
                        }}
                      >
                        {job.isHidden ? "Hidden" : "Visible"}
                      </span>
                    </td>
                    <td className="action_buttons">
                      {/* Visibility quick-toggle */}
                      <button
                        title={job.isHidden ? "Show this job" : "Hide this job"}
                        onClick={() => handleToggleVisibility(job)}
                      >
                        <i 
                          className={`fas ${job.isHidden ? 'fa-eye-slash' : 'fa-eye'}`} 
                          style={{ fontSize: '18px', width: '20px', textAlign: 'center' }}
                        ></i>
                      </button>

                      <button onClick={() => handleEdit(job)}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 512 512"
                        >
                          <path
                            fill="currentColor"
                            d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1L377.9 88L407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9l46.1 46.1l-134.3 134.2c-2.9 2.9-6.5 5-10.4 6.1L186.9 325l16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25c-28.1-28.1-73.7-28.1-101.8 0M88 64c-48.6 0-88 39.4-88 88v272c0 48.6 39.4 88 88 88h272c48.6 0 88-39.4 88-88V312c0-13.3-10.7-24-24-24s-24 10.7-24 24v112c0 22.1-17.9 40-40 40H88c-22.1 0-40-17.9-40-40V152c0-22.1 17.9-40 40-40h112c13.3 0 24-10.7 24-24s-10.7-24-24-24z"
                          />
                        </svg>
                      </button>

                      <button onClick={() => handleDelete(job.id)}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                        >
                          <path
                            fill="currentColor"
                            d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zm2.46-7.12l1.41-1.41L12 12.59l2.12-2.12l1.41 1.41L13.41 14l2.12 2.12l-1.41 1.41L12 15.41l-2.12 2.12l-1.41-1.41L10.59 14zM15.5 4l-1-1h-5l-1 1H5v2h14V4z"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostJob;
